import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useCallback, useEffect, useState } from "react";
import { useReportExport } from "@/hooks/useReportExport";

type ExportRow = {
  chart: string;
  group: string;
  value: string | number;
};

/**
 * Exports what is actually on the user's dashboards: every saved chart is
 * flattened into rows, then written out as CSV, Excel or the full PDF report.
 */
export const ExportOptions = () => {
  const [isExporting, setIsExporting] = useState<string | null>(null);
  const [rows, setRows] = useState<ExportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const { generateReport, isGenerating } = useReportExport();

  const loadRows = useCallback(async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setRows([]);
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from("saved_charts")
      .select("title, data")
      .eq("user_id", user.id)
      .order("position", { ascending: true });

    const flattened: ExportRow[] = [];
    (data ?? []).forEach((chart) => {
      const points = Array.isArray(chart.data) ? chart.data : [];
      points.forEach((raw) => {
        if (!raw || typeof raw !== "object" || Array.isArray(raw)) return;
        const point = raw as Record<string, unknown>;
        const keys = Object.keys(point);
        const nameKey = keys.find((k) => typeof point[k] === "string") ?? keys[0];
        const valueKey = keys.find((k) => typeof point[k] === "number") ?? keys[1];
        flattened.push({
          chart: chart.title,
          group: String(point[nameKey] ?? ""),
          value: (point[valueKey] as string | number) ?? "",
        });
      });
    });

    setRows(flattened);
    setLoading(false);
  }, []);

  useEffect(() => { loadRows(); }, [loadRows]);

  const triggerDownload = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExport = async (format: "csv" | "excel") => {
    if (!rows.length) {
      toast.error("There are no saved dashboard charts to export yet.");
      return;
    }

    setIsExporting(format);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please sign in to export your data");
        return;
      }

      const header = ["Chart", "Group", "Value"];
      const body = rows.map((r) => [r.chart, r.group, String(r.value)]);

      const content = format === "csv"
        ? [header, ...body].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n")
        : [header, ...body].map((row) => row.join("\t")).join("\n");

      const stamp = new Date().toISOString().split("T")[0];
      const extension = format === "csv" ? "csv" : "xls";
      const mimeType = format === "csv" ? "text/csv" : "application/vnd.ms-excel";

      triggerDownload(content, `dashboard-export-${stamp}.${extension}`, mimeType);

      await supabase.from("exports").insert({
        user_id: user.id,
        resource_type: "dashboard",
        resource_id: "saved-charts",
        format,
        status: "completed",
      });
      await supabase.from("system_logs").insert({
        user_id: user.id,
        action: "data_export",
        status: "success",
        description: `Dashboard data exported as ${format.toUpperCase()}`,
        metadata: { format, rows_exported: rows.length },
      });

      toast.success(`${format.toUpperCase()} export downloaded.`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Could not export your data. Please try again.");
    } finally {
      setIsExporting(null);
    }
  };

  const handlePdf = () => {
    if (!rows.length) {
      toast.error("There are no saved dashboard charts to report on yet.");
      return;
    }
    generateReport({
      title: "Dashboard report",
      datasetName: "Dashboard charts",
      rows: rows as unknown as Record<string, unknown>[],
    });
  };

  const busy = isExporting !== null || isGenerating || loading;

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold">Export Options</h2>
          <p className="text-sm text-muted-foreground">
            {loading
              ? "Checking your saved charts…"
              : rows.length
                ? `Export the ${rows.length} data points behind your saved charts.`
                : "Save a chart to a dashboard first — exports use your real chart data."}
          </p>
        </div>
        <Download className="h-6 w-6 text-muted-foreground" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Button
          variant="outline"
          className="justify-start"
          onClick={() => handleExport("csv")}
          disabled={busy || !rows.length}
        >
          {isExporting === "csv" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FileSpreadsheet className="mr-2 h-4 w-4" />
          )}
          {isExporting === "csv" ? "Exporting..." : "Export as CSV"}
        </Button>

        <Button
          variant="outline"
          className="justify-start"
          onClick={() => handleExport("excel")}
          disabled={busy || !rows.length}
        >
          {isExporting === "excel" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FileSpreadsheet className="mr-2 h-4 w-4" />
          )}
          {isExporting === "excel" ? "Exporting..." : "Export as Excel"}
        </Button>

        <Button
          variant="outline"
          className="justify-start"
          onClick={handlePdf}
          disabled={busy || !rows.length}
        >
          {isGenerating ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FileText className="mr-2 h-4 w-4" />
          )}
          {isGenerating ? "Building report..." : "Download PDF report"}
        </Button>
      </div>

      <div className="mt-4 p-3 rounded-lg border bg-muted/40">
        <p className="text-sm text-muted-foreground">
          The PDF is a full business report: headline figures, a data table, grouped
          breakdowns with charts, written observations, recommendations and a conclusion.
        </p>
      </div>
    </Card>
  );
};
